import { UserAssociation } from "@boark/shared/types/auth-context";
import { errorResponseSchema } from "@boark/shared/types/error-response";
import { Page } from "@boark/shared/types/page";
import { useRef, useState } from "react";
import { useEffect } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { FETCH_ERROR } from "../consts/fetch-error";
import { useAlertMessage } from "../hooks/alert-message";
import { useGetAssociationContext } from "../queries/get-association-context";
import { useGetPages } from "../queries/get-pages";
import { logout } from "../services/auth";
import { on } from "../services/event-listner";
import { getSubPages } from "../services/page";
import { useAuthStore } from "../stores/auth";
import { useLocale } from "../stores/locale";
import { Alert } from "./alert";
import { Container } from "./container";
import { Layout } from "./layout";
import { Menu, MenuItem } from "./navbar";
import { SubMenu } from "./sub-menu";

export function WebPage({ children }: { children?: React.ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const t = useLocale((state) => state.locale);
  const { setLocale } = useLocale();
  const authStore = useAuthStore();
  const alertMessage = useAlertMessage();

  const [searchParams] = useSearchParams();
  const query = searchParams.get("query");

  const setIsAuthRef = useRef(authStore.setIsAuth);
  const navigateRef = useRef(navigate);

  const { data: pages, error: pageError } = useGetPages();
  const { data: associationData, error: associationError } =
    useGetAssociationContext();

  const [slug1, setSlug1] = useState<string | null>(null);
  const [menu, setMenu] = useState<MenuItem[]>([]);
  const [subMenu, setSubMenu] = useState<MenuItem[]>([]);
  const [profileMenu, setProfileMenu] = useState<Menu[] | null>(null);
  const [association, setAssociation] = useState<UserAssociation | null>(null);
  const [searchValue, setSearchValue] = useState<string>("");

  // Set window title
  useEffect(() => {
    if (associationData) {
      document.title = `${associationData.name}`;
    }
  }, [associationData]);

  // Update search value when query changes
  useEffect(() => {
    setSearchValue(query ?? "");
  }, [query]);

  // Logout and redirect to home if fetch error is encountered
  useEffect(() => {
    if (
      pageError === FETCH_ERROR.UNAUTHORIZED ||
      associationError === FETCH_ERROR.UNAUTHORIZED
    ) {
      (async () => {
        await logout();
        setIsAuthRef.current(false);
        navigateRef.current("/");
      })();
    }
  }, [pageError, associationError]);

  useEffect(() => {
    const subs = on("error", (payload: unknown) => {
      const parsedPayload = errorResponseSchema.parse(payload);

      if (parsedPayload.code === FETCH_ERROR.UNAUTHORIZED.toString()) {
        logout();
        setIsAuthRef.current(false);
        navigateRef.current("/");
      }

      alertMessage.open(parsedPayload);
      alertMessage.setIsOpen(true);
    });

    return () => {
      subs.unsubscribe();
    };
  });

  // Set slug1 from location pathname
  useEffect(() => {
    const slug1 = location.pathname.split("/")[1] || null;
    setSlug1(slug1);
  }, [location.pathname]);

  // Update menu when loaded pages change
  useEffect(() => {
    if (!pages) return;

    const menu = pagesToTree(pages);
    const authMenu: MenuItem[] = authStore.isAuth
      ? [
          { separator: true },
          {
            slug: "/admin",
            title: t.admin.admin,
            children: [
              {
                slug: "/admin/register",
                title: t.admin.register,
              },
              {
                slug: "/admin/contract",
                title: t.admin.contract,
              },
              {
                slug: "/admin/procurement",
                title: t.admin.procurement,
              },
              {
                slug: "/admin/board-meeting",
                title: t.admin.boardMeetings,
              },
              {
                slug: "/admin/issue",
                title: t.admin.issues,
              },
              {
                slug: "/admin/maintenance",
                title: t.admin.maintenance,
              },
              {
                slug: "/admin/role",
                title: t.admin.roles,
              },
              {
                slug: "/admin/user",
                title: t.admin.users,
              },
            ],
          },
        ]
      : [];
    setMenu([...menu, ...authMenu]);
  }, [pages, authStore.isAuth, t]);

  // Update association when loaded association data changes
  useEffect(() => {
    if (!associationData) return;

    setAssociation(associationData);
  }, [associationData]);

  // Redirect to first menu item if path is /
  useEffect(() => {
    if (location.pathname === "/") {
      const firstMenuItem = menu.find((item) => !("separator" in item));
      if (firstMenuItem && "slug" in firstMenuItem) {
        navigateRef.current(firstMenuItem.redirectTo || firstMenuItem.slug);
      }
    }
  }, [location.pathname, menu]);

  // Update submenu when slug1 changes
  useEffect(() => {
    if (!slug1) {
      setSubMenu([]);
      return;
    }
    setSubMenu(getSubMenu(menu, slug1));
  }, [slug1, menu]);

  useEffect(() => {
    if (authStore.isAuth) {
      setProfileMenu([
        {
          slug: "/logout",
          title: t.logout,
        },
      ]);
    } else {
      setProfileMenu(null);
    }
  }, [authStore.isAuth, t.logout]);

  if (!association) return null;

  const color = "pink";

  const CDN_BASE_URL = process.env.CDN_BASE_URL;

  return (
    <Layout
      availableLanguages={association.availableLanguages}
      color={color}
      contacts={association.contacts}
      logoUrl={`${CDN_BASE_URL}/${association.domain}/uploads/public/logo.png`}
      menu={menu}
      name={association.name}
      organizationNumber={association.organizationNumber}
      profileMenu={profileMenu || undefined}
      searchValue={searchValue}
      socialLinks={association.socialLinks}
      onLanguageChange={(language: string) => {
        setLocale(language);
      }}
      onSearch={(query: string) => {
        navigate(`/search?query=${query}`);
      }}
    >
      <Alert
        header={alertMessage.header}
        isOpen={alertMessage.isOpen}
        message={alertMessage.message}
        onClose={() => alertMessage.setIsOpen(false)}
      />
      <Container className="flex gap-5">
        {subMenu.length > 0 && (
          <div className="hidden md:block min-w-[230px]">
            <SubMenu color={color} menu={subMenu} />
          </div>
        )}
        <div className="flex-1 min-w-0">{children}</div>
      </Container>
    </Layout>
  );
}

function pagesToTree(pages: Page[]): MenuItem[] {
  return pages
    .filter((page) => page.slug.split("/").length === 2)
    .sort((a, b) => a.order - b.order)
    .map((page) => {
      const subMenu = getSubPages(pages, page.slug);
      return {
        ...page,
        ...(subMenu.length > 0 && subMenu[0]
          ? {
              redirectTo: `${subMenu[0].slug}`,
            }
          : {}),
        children: subMenu.sort((a, b) => a.order - b.order),
      };
    });
}

function getSubMenu(menu: MenuItem[], slug: string): MenuItem[] {
  const page = menu.find((item) => "slug" in item && item.slug === `/${slug}`);
  if (!page || !("children" in page)) {
    return [];
  }

  return page.children || [];
}
